import React, { useEffect, useRef, useState } from 'react'
import { CgPen, CgTrash, CgComment, CgChevronDown, CgChevronUp } from 'react-icons/cg'
import { BsPin, BsPinFill, BsCheck2, BsCheck2All, BsHeart, BsHeartFill } from 'react-icons/bs'
import { MdPushPin, MdCommentsDisabled } from 'react-icons/md'

import { useAlert } from 'common/components/Alert/hooks/useAlert'
import { Dialog, DialogContent, DialogTrigger } from 'common/components/Dialog/Dialog'
import { DeleteDialogBanner } from 'common/components/DeleteDialogBanner/DeleteDialogBanner'
import { FormRefsControlProvider } from 'common/components/RefsControl/FormRefsControl/context'
import Tag from 'common/components/Tag/Tag'

import {
  useDeleteCommunityPostMutation,
  useListenMyCommunityLikesQuery,
  useMarkPostsDoneMutation,
  usePinCommunityPostMutation,
  useToggleCommunityPostLikeMutation,
} from '../communityApi'
import { isPostPinned } from '../communityUtils'
import { hasPostButton } from '../postButtonUtils'
import { PIN_DURATION_MS } from '../communityConstants'
import CommunityPostForm from '../CommunityPostForm/CommunityPostForm'
import useCoachAuthor from '../useCoachAuthor'
import formatFeedTime from './formatFeedTime'
import PostAttachmentCard, { usePostAttachment } from './PostAttachmentCard'
import PostAvatar from './PostAvatar'
import PostThread from './PostThread'
import { BanMemberButton, MemberName } from './MemberActions'

function Post({ post, coachOrgId, userId, inboxMode = false, onMarkOlderDone }) {
  const { createAlert } = useAlert()

  const [editDialogOpen, setEditDialogOpen] = useState(false)
  // In the Needs reply tab, threads start open so the coach can read and reply
  // without an extra click per post.
  const [expanded, setExpanded] = useState(inboxMode)
  const [loading, setLoading] = useState(false)

  // Auto-opened threads only fetch their replies once the row scrolls near the
  // viewport — a long Needs reply queue loads as the coach works through it,
  // not all at once on tab open. Click-to-expand rows fetch immediately.
  const rowRef = useRef(null)
  const [isNearViewport, setIsNearViewport] = useState(!inboxMode)
  useEffect(() => {
    if (isNearViewport) return
    const node = rowRef.current
    if (!node || typeof IntersectionObserver === 'undefined') {
      setIsNearViewport(true)
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setIsNearViewport(true)
      },
      { rootMargin: '300px' }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [isNearViewport])

  const [deleteCommunityPost] = useDeleteCommunityPostMutation()
  const [pinCommunityPost] = usePinCommunityPostMutation()
  const [markPostsDone] = useMarkPostsDoneMutation()
  const [togglePostLike] = useToggleCommunityPostLikeMutation()

  const author = useCoachAuthor({ coachOrgId, userId })
  const { data: myLikesData } = useListenMyCommunityLikesQuery({ coachOrgId, userId })
  const myLikes = myLikesData?.isLoading ? {} : myLikesData || {}
  const isLiked = Boolean(myLikes?.posts?.[post.id])

  const isPinned = isPostPinned(post, Date.now())
  const isOwnPost = post.authorId === userId
  // Every button type, links included, renders as an attachment card below.
  const attachment = usePostAttachment(post, coachOrgId)

  const handleDelete = async () => {
    setLoading(true)
    const result = await deleteCommunityPost({ coachOrgId, postId: post.id })
    setLoading(false)
    if (result?.error || result?.data?.error) {
      createAlert({ text: 'Could not delete post. Please try again.', type: 'danger' })
      return
    }
    createAlert({ text: 'Post deleted!', type: 'success' })
  }

  const handleTogglePin = async () => {
    const pinnedUntil = isPinned ? null : Date.now() + PIN_DURATION_MS
    const result = await pinCommunityPost({ coachOrgId, postId: post.id, pinnedUntil })
    if (result?.error || result?.data?.error) {
      createAlert({ text: 'Could not update pin. Please try again.', type: 'danger' })
      return
    }
    createAlert({ text: isPinned ? 'Post unpinned' : 'Post pinned to top', type: 'success' })
  }

  const handleToggleLike = () => {
    togglePostLike({
      coachOrgId,
      postId: post.id,
      postAuthorId: post.authorId,
      userId,
      actorName: author.displayName,
      isLiked,
    })
  }

  const handleMarkDone = async () => {
    const result = await markPostsDone({ coachOrgId, postIds: [post.id] })
    if (result?.error || result?.data?.error) {
      createAlert({ text: 'Could not mark as done. Please try again.', type: 'danger' })
      return
    }
    createAlert({ text: 'Marked as done', type: 'success' })
  }

  return (
    <div
      ref={rowRef}
      data-testid='community-post'
      className='relative flex flex-col rounded-2xl bg-white w-auto mb-3 group overflow-hidden'
    >
      <div className='flex flex-1 p-6 pb-4'>
        <div className='flex flex-col flex-1 min-w-0'>
          <div className='flex items-center mb-3'>
            <PostAvatar name={post?.authorName} picture={post?.authorPicture} color={post?.authorColor} size={40} />
            <div className='ml-3 min-w-0'>
              <div className='flex items-center'>
                <MemberName
                  coachOrgId={coachOrgId}
                  userId={post?.authorId}
                  name={post?.authorName}
                  isCoach={post?.authorIsCoach === true}
                  className='font-bold text-tBlack truncate'
                />
                <div className='flex items-center gap-1.5 ml-3'>
                  {post?.authorIsCoach && <Tag text='Coach' className='!mb-0 !text-xs !rounded-md' />}
                  {isPinned && <MdPushPin className='w-4 h-4 text-gray-500' title='Pinned' />}
                </div>
              </div>
              <div className='text-xs text-gray-500'>
                {formatFeedTime(post?.createdAt)}
                {post?.editedAt ? ' (edited)' : ''}
              </div>
            </div>
          </div>
          <div className='text-sm text-tGray-dark whitespace-pre-wrap break-words'>{post?.text}</div>
          {post?.imageUrl && !hasPostButton(post) && (
            <div className='relative w-80 max-w-full aspect-video rounded-xl overflow-hidden mt-3'>
              <img src={post.imageUrl} alt='post' className='w-full h-full object-cover' />
            </div>
          )}
          <PostAttachmentCard attachment={attachment} />
          <div className='flex items-center text-xs text-gray-500 mt-3 pt-3 border-t border-gray-100 space-x-4'>
            <button
              className='inline-flex items-center hover:text-tRed transition-colors'
              onClick={handleToggleLike}
              title={isLiked ? 'Unlike' : 'Like'}
            >
              {isLiked ? (
                <BsHeartFill className='w-3.5 h-3.5 mr-1 text-tRed' />
              ) : (
                <BsHeart className='w-3.5 h-3.5 mr-1' />
              )}
              {post?.likeCount || 0}
            </button>
            {post?.repliesOff === true ? (
              <MdCommentsDisabled className='w-4 h-4 text-gray-400' title='Replies are turned off' />
            ) : (
              <button
                className='inline-flex items-center hover:text-tGreen transition-colors'
                onClick={() => setExpanded(!expanded)}
                title={expanded ? 'Hide replies' : 'View replies and reply'}
              >
                <CgComment className='w-4 h-4 mr-1' /> {post?.replyCount || 0}
                {expanded ? <CgChevronUp className='w-4 h-4 ml-0.5' /> : <CgChevronDown className='w-4 h-4 ml-0.5' />}
              </button>
            )}
          </div>
        </div>
      </div>
      {expanded && post?.repliesOff !== true && isNearViewport && (
        <PostThread post={post} coachOrgId={coachOrgId} userId={userId} />
      )}
      <div className='absolute top-5 right-4 inline-flex items-center justify-end bg-white group-hover:border group-hover:border-tBlack group-hover:border-opacity-20 rounded-lg group-hover:shadow-sm'>
        {inboxMode && (
          <>
            <button
              onClick={handleMarkDone}
              title='Mark as done'
              className='hidden group-hover:inline-block hover:text-tGreen py-1 px-2 border-r border-tBlack border-opacity-20 transition-all'
            >
              <BsCheck2 className='w-[18px] h-[18px]' />
            </button>
            <button
              onClick={() => onMarkOlderDone?.(post)}
              title='Mark this and older as done'
              className='hidden group-hover:inline-block hover:text-tGreen py-1 px-2 border-r border-tBlack border-opacity-20 transition-all'
            >
              <BsCheck2All className='w-[18px] h-[18px]' />
            </button>
          </>
        )}
        {/* No ban on a coach's post, as in the app: a coach could unban themselves
            anyway, so it would only ever be an accidental mute. */}
        {!isOwnPost && post?.authorIsCoach !== true && (
          <BanMemberButton
            coachOrgId={coachOrgId}
            userId={post.authorId}
            name={post.authorName}
            className='hidden group-hover:inline-block hover:text-tRed py-1 px-2 border-r border-tBlack border-opacity-20 transition-all'
          />
        )}
        <button
          onClick={handleTogglePin}
          title={isPinned ? 'Unpin post' : 'Pin post to top'}
          className={`hidden group-hover:inline-block py-1 px-2 border-r border-tBlack border-opacity-20 transition-all ${
            isPinned ? 'hover:text-tRed' : 'hover:text-tGreen'
          }`}
        >
          {isPinned ? <BsPinFill className='w-[18px] h-[18px]' /> : <BsPin className='w-[18px] h-[18px]' />}
        </button>
        {isOwnPost && (
          <Dialog open={editDialogOpen} setOpen={setEditDialogOpen}>
            <DialogTrigger
              title='Edit post'
              className='hidden group-hover:inline-block hover:text-tGreen py-1 px-2 border-r border-tBlack border-opacity-20 transition-all'
            >
              <CgPen className='w-5 h-5' />
            </DialogTrigger>
            <DialogContent header='Edit post'>
              <FormRefsControlProvider>
                <CommunityPostForm post={post} coachOrgId={coachOrgId} userId={userId} />
              </FormRefsControlProvider>
            </DialogContent>
          </Dialog>
        )}
        <Dialog>
          <DialogTrigger
            title='Delete post'
            className='hidden group-hover:inline-block hover:text-tRed py-1 px-2 transition-all'
          >
            <CgTrash className='w-5 h-5' />
          </DialogTrigger>
          <DialogContent>
            <DeleteDialogBanner
              text='This will delete the post and all its replies'
              handleDelete={handleDelete}
              loading={loading}
            />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}

export default Post
